import { PRIVILEGE_ALL_ACTIONS, PRIVILEGE_SLUG } from "../constants";

const asArray = (value) => (Array.isArray(value) ? value : []);

export const normalizePrivileges = (input) => {
  if (!input || typeof input !== "object" || Array.isArray(input)) return {};

  return Object.entries(input).reduce((acc, [privilegeSlug, actions]) => {
    if (!privilegeSlug) return acc;
    const normalizedActions = [...new Set(asArray(actions).filter(Boolean))];
    if (normalizedActions.length > 0) {
      acc[privilegeSlug] = normalizedActions;
    }
    return acc;
  }, {});
};

const getPrivileges = (privileges, privilegeSlug) => {
  const normalized = normalizePrivileges(privileges);
  return asArray(normalized[privilegeSlug]);
};

/**
 * @param {Record<string, string[]>} privileges
 * @param {string|string[]|Record<string, string|string[]>} privilegeSlugOrSlugs - Single slug, multiple slugs, or a requirements map.
 * @param {string} [action]
 */
export const hasPrivilege = (privileges, privilegeSlugOrSlugs, action) => {
  if (action !== undefined) {
    return getPrivileges(privileges, privilegeSlugOrSlugs).includes(action);
  }

  if (isRequirementsMap(privilegeSlugOrSlugs)) {
    return Object.entries(privilegeSlugOrSlugs).some(([privilegeSlug, requiredPrivileges]) => {
      const requiredActions = toActionList(requiredPrivileges);
      if (requiredActions.length === 0) return getPrivileges(privileges, privilegeSlug).length > 0;
      const granted = getPrivileges(privileges, privilegeSlug);
      return requiredActions.some((a) => granted.includes(a));
    });
  }

  if (Array.isArray(privilegeSlugOrSlugs)) {
    return privilegeSlugOrSlugs.some(
      (privilegeSlug) => getPrivileges(privileges, privilegeSlug).length > 0
    );
  }
  return getPrivileges(privileges, privilegeSlugOrSlugs).length > 0;
};

const toActionList = (value) => {
  if (typeof value === "string") return value ? [value] : [];
  return asArray(value).filter(Boolean);
};

const isRequirementsMap = (value) =>
  value != null && typeof value === "object" && !Array.isArray(value);

/**
 * Company superadmin routes: granted if either `companies` or legacy `organization_management`
 * includes the requested operation (aligned with API OR across those slugs).
 */
export const hasCompaniesOrOrganisationManagementPrivilege = (privileges, operation) =>
  hasPrivilege(privileges, PRIVILEGE_SLUG.companies, operation) ||
  hasPrivilege(privileges, PRIVILEGE_SLUG.organizationManagement, operation);

const expandActions = (actions = []) => {
  const normalized = asArray(actions);
  if (normalized.length === 0) return [...PRIVILEGE_ALL_ACTIONS];
  return [...new Set(normalized)];
};

/** Intersection of expanded action lists; order follows PRIVILEGE_ALL_ACTIONS. */
const intersectExpandedActionLists = (lists) => {
  if (lists.length === 0) return [];
  const sets = lists.map((list) => new Set(list));
  return PRIVILEGE_ALL_ACTIONS.filter((a) => sets.every((s) => s.has(a)));
};

/**
 * Builds { [slug]: actions[] } from role array / userRole array payloads.
 * For each slug, intersects expanded actions across every role that defines that slug
 * (null/empty actions on a role expand to all actions first, then intersect).
 */
export const buildPrivilegesMapFromRoles = (roles = []) => {
  /** @type {Record<string, string[][]>} */
  const bySlug = {};

  asArray(roles).forEach((roleLike) => {
    const role = roleLike?.Role || roleLike;
    const rolePrivileges = asArray(role?.privileges);
    rolePrivileges.forEach((privilege) => {
      const privilegeSlug = privilege?.slug;
      if (!privilegeSlug) return;
      const allowedActions =
        privilege?.RolePrivilege?.allowedActions ||
        privilege?.rolePrivilege?.allowedActions ||
        privilege?.role_privilege?.allowedActions;
      const expandedActions = expandActions(allowedActions);
      if (!bySlug[privilegeSlug]) bySlug[privilegeSlug] = [];
      bySlug[privilegeSlug].push(expandedActions);
    });
  });

  const out = {};
  Object.entries(bySlug).forEach(([slug, lists]) => {
    const merged = intersectExpandedActionLists(lists);
    if (merged.length > 0) {
      out[slug] = merged;
    }
  });
  return out;
};

/**
 * Resolves a { [slug]: actions[] } map from SSR/session user payloads.
 * Prefers top-level `privileges` (JWT-style) when present.
 * Otherwise intersects privileges across `user.roles` and each `company.userRoles[].Role`.
 */
export const buildPrivilegesMapFromSessionUser = (user) => {
  if (!user || typeof user !== "object") return {};
  if (
    user.privileges &&
    typeof user.privileges === "object" &&
    !Array.isArray(user.privileges)
  ) {
    return { ...user.privileges };
  }

  const platformRoles = asArray(user.roles);
  const companyRoles = asArray(user.company).flatMap((comp) =>
    asArray(comp.userRoles)
      .map((ur) => ur?.Role)
      .filter(Boolean)
  );

  return buildPrivilegesMapFromRoles([...platformRoles, ...companyRoles]);
};

/** Normalized privilege map for UI checks (SessionContext / SSR user). */
export const getSessionPrivilegesMap = (user) =>
  normalizePrivileges(buildPrivilegesMapFromSessionUser(user));
